import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  Users,
  TrendingUp,
  Package,
  Award,
  ChevronRight,
  Star,
  Shield,
  Menu,
  X,
  Bot,
  Calendar,
  Mail,
  ShoppingBag,
  Truck,
  CheckCircle,
  Building2,
  BadgeCheck,
  Target,
  Zap,
  Globe,
  BarChart3,
  Grid3X3,
  Layers,
  Cpu,
  Network,
  Brain,
  Sparkles,
  CircuitBoard,
  Binary,
  Rocket,
  DollarSign,
  ArrowUpRight,
  Play,
  Phone,
  MessageCircle,
  PartyPopper
} from "lucide-react";

const LOGO_URL = "/achraj_logo.png";
const CONTACT_EMAIL = "achrajaigroup@gmail.com";

const AI_FEATURES = [
  { icon: Brain, title: "AI-Powered Matrix", desc: "Neural network aapka 20-level binary tree automatically banata hai", stat: "20 Levels" },
  { icon: Cpu, title: "Smart Auto-Fill", desc: "AI 24/7 global registrations se positions fill karta hai", stat: "24/7 Active" },
  { icon: Network, title: "Network Intelligence", desc: "Machine learning aapki earning potential ko optimize karta hai", stat: "₹4.19 Cr Max" },
  { icon: Zap, title: "Instant Calculations", desc: "Real-time commission tracking aur payouts", stat: "Real-time" },
];

const PLANS = [
  { type: "D", price: "₹500", levels: 17, income: "₹52.42L", color: "from-slate-500 to-slate-600", popular: false },
  { type: "C", price: "₹1,000", levels: 18, income: "₹1.04Cr", color: "from-cyan-500 to-blue-600", popular: true },
  { type: "B", price: "₹2,000", levels: 19, income: "₹2.09Cr", color: "from-purple-500 to-violet-600", popular: false },
  { type: "A", price: "₹4,000", levels: 20, income: "₹4.19Cr", color: "from-amber-500 to-orange-500", popular: false },
];

const HINGLISH_QUOTES = [
  "Ghar baithe kamaai, bas ek ID se shuru karo",
  "Sapne bade dekho, network bada banao",
  "10000+ logon ka bharosa, ab aapki baari",
  "Mehnat ek baar, kamaai baar baar"
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`)
        ]);
        const cats = categoriesRes.data || [];
        setCategories(cats);
        const allProducts = productsRes.data || [];
        const catProds = {};
        for (const product of allProducts) {
          if (product.category_id && !catProds[product.category_id]) {
            catProds[product.category_id] = product;
          }
        }
        setCategoryProducts(catProds);
      } catch (err) { console.error("Error fetching data:", err); }
    };
    fetchData();
  }, []);

  const getDashboardLink = () => {
    if (!user) return "/login";
    if (user.role === "admin") return "/admin";
    if (user.role === "franchise") return "/franchise";
    return "/member";
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="landing-page">
      {/* Navigation - Light */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="Achraj Logo" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover border-2 border-orange-200" />
              <div className="hidden sm:block">
                <span className="font-bold text-lg text-gray-900">Achraj Services</span>
                <p className="text-xs text-gray-500">Private Limited</p>
              </div>
            </Link>

            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <Link to="/products" className="text-gray-600 hover:text-orange-600 transition-colors font-medium text-sm">Products</Link>
              <Link to="/ai-income-plan" className="text-gray-600 hover:text-orange-600 transition-colors font-medium text-sm">Income Plan</Link>
              <a href="#ai-power" className="text-gray-600 hover:text-orange-600 transition-colors font-medium text-sm">AI Power</a>
              <a href="#about" className="text-gray-600 hover:text-orange-600 transition-colors font-medium text-sm">About</a>
              {user ? (
                <Button onClick={() => navigate(getDashboardLink())} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6" data-testid="dashboard-btn">
                  Dashboard
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login"><Button variant="ghost" className="text-gray-600 hover:text-orange-600" data-testid="login-btn">Login</Button></Link>
                  <Link to="/register"><Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6" data-testid="join-btn">Join Now</Button></Link>
                </div>
              )}
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4 px-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-3">
              <Link to="/products" className="text-gray-700 py-2 px-3 font-medium rounded-lg hover:bg-orange-50">Products</Link>
              <Link to="/ai-income-plan" className="text-gray-700 py-2 px-3 font-medium rounded-lg hover:bg-orange-50">Income Plan</Link>
              <a href="#ai-power" className="text-gray-700 py-2 px-3 font-medium rounded-lg hover:bg-orange-50" onClick={() => setMobileMenuOpen(false)}>AI Power</a>
              <a href="#about" className="text-gray-700 py-2 px-3 font-medium rounded-lg hover:bg-orange-50" onClick={() => setMobileMenuOpen(false)}>About</a>
              <div className="border-t border-gray-200 pt-3 mt-2 flex flex-col gap-2">
                {user ? (
                  <Button onClick={() => navigate(getDashboardLink())} className="bg-orange-500 text-white rounded-full w-full">Dashboard</Button>
                ) : (
                  <>
                    <Link to="/login" className="w-full"><Button variant="outline" className="w-full border-orange-300 text-orange-600 rounded-full">Login</Button></Link>
                    <Link to="/register" className="w-full"><Button className="bg-orange-500 text-white rounded-full w-full">Join Now</Button></Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Light Theme with Hinglish */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 relative overflow-hidden bg-gradient-to-b from-orange-50 via-white to-white" data-testid="hero-section">
        {/* Subtle background decoration */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-orange-100 rounded-full blur-[120px] opacity-60"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-100 rounded-full blur-[120px] opacity-40"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* 10000+ Family Celebration Banner */}
            <div className="mb-6 flex justify-center" data-testid="family-celebration-banner">
              <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-gradient-to-r from-orange-500 via-amber-500 to-purple-600 text-white shadow-lg shadow-orange-200 animate-pulse">
                <PartyPopper className="w-5 h-5 shrink-0" />
                <p className="text-sm sm:text-base font-semibold leading-tight">
                  Badhai ho! Hum ab <span className="font-extrabold">10000+ ka parivaar</span> ho gaye hain — Achraj Team ko dhanyawaad!
                </p>
                <PartyPopper className="w-5 h-5 shrink-0 scale-x-[-1]" />
              </div>
            </div>

            {/* Hinglish Quote Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-100 border border-orange-200 text-orange-700 rounded-full text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Ghar baithe kamaai, bas ek ID se shuru karo</span>
            </div>
            
            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight mb-6">
              <span className="text-gray-900">Apna </span>
              <span className="bg-gradient-to-r from-orange-500 to-purple-600 bg-clip-text text-transparent">AI Network</span>
              <br />
              <span className="text-gray-900">Banao, Paisa Kamao</span>
            </h1>
            
            {/* Subtitle in Hinglish */}
            <p className="text-lg sm:text-xl text-gray-600 mb-4 max-w-2xl mx-auto leading-relaxed">
              Hamara <span className="text-orange-600 font-semibold">AI system</span> aapke liye 20-level network automatically build karta hai.
              <span className="text-purple-600 font-semibold"> Koi manual recruiting ki zaroorat nahi!</span>
            </p>
            <p className="text-base text-gray-500 mb-8 italic">"Sapne bade dekho, network bada banao"</p>
            
            {/* Stats Row */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-10">
              {[
                { value: "₹4.19 Cr", label: "Max Earning", icon: DollarSign },
                { value: "20", label: "AI Levels", icon: Network },
                { value: "10000+", label: "Members", icon: Users },
                { value: "24/7", label: "AI Active", icon: Cpu },
              ].map((stat, idx) => (
                <div key={idx} className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-lg sm:text-xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-xs text-gray-500">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link to="/register">
                <Button className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-orange-200 hover:shadow-orange-300 transition-all" data-testid="hero-cta-btn">
                  <Rocket className="mr-2 w-5 h-5" /> Abhi Join Karo
                </Button>
              </Link>
              <Link to="/ai-income-plan">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-purple-300 text-purple-600 hover:bg-purple-50 rounded-full px-8 py-6 text-lg font-semibold">
                  <Play className="mr-2 w-5 h-5" /> Income Plan Dekho
                </Button>
              </Link>
            </div>
            
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-full border border-gray-200">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>10000+ logon ka bharosa | MCA Registered Company</span>
            </div>
          </div>
        </div>
      </section>

      {/* AI Power Section */}
      <section id="ai-power" className="py-16 sm:py-24 px-4 bg-gray-50" data-testid="ai-power-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 border border-purple-200 text-purple-700 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" /> Advanced Technology
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              AI Ki Taakat Se <span className="text-orange-500">Kamao</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">Hamara AI system din-raat kaam karta hai taaki aapki earning maximum ho</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {AI_FEATURES.map((feature, idx) => (
              <Card key={idx} className="bg-white border border-gray-200 hover:border-orange-300 rounded-2xl overflow-hidden transition-all hover:shadow-lg group">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <feature.icon className="w-7 h-7 text-orange-600" />
                  </div>
                  <div className="text-xs text-orange-600 font-semibold mb-2">{feature.stat}</div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-500">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Kaise Kaam Karta Hai?</h2>
            <p className="text-gray-500">Sirf 4 simple steps mein AI-powered income shuru karo</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { step: "01", title: "Join Karo", desc: "Apna plan choose karo aur AI slot activate karo", icon: Rocket },
              { step: "02", title: "AI Activate", desc: "Aapka personal AI network building shuru karega", icon: Cpu },
              { step: "03", title: "Auto-Fill Magic", desc: "AI aapka 20-level tree 24/7 fill karta hai", icon: Network },
              { step: "04", title: "Kamaai Shuru", desc: "Automatically income badhti jaaye", icon: DollarSign },
            ].map((item, idx) => (
              <div key={idx} className="relative group">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 text-6xl sm:text-8xl font-bold text-orange-50 select-none">{item.step}</div>
                <div className="relative bg-white border border-gray-200 rounded-2xl p-6 text-center hover:border-orange-300 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-orange-500 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-200">
                    <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-16 sm:py-24 px-4 bg-gray-50" data-testid="products-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 border border-orange-200 text-orange-700 rounded-full text-sm font-medium mb-4">
              <Grid3X3 className="w-4 h-4" /> Hamari Products
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Category Se Shop Karo</h2>
            <p className="text-gray-500">Premium quality products wide range mein available</p>
          </div>
          
          {categories.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
              {categories.map((category, idx) => {
                const previewProduct = categoryProducts[category.id];
                const previewImg = previewProduct?.image_url || (previewProduct?.images && previewProduct.images.length > 0 ? (previewProduct.images[0]?.url || previewProduct.images[0]) : null) || category.image_url;
                
                return (
                  <div key={category.id || idx} className="group relative overflow-hidden rounded-2xl bg-white border border-gray-200 hover:border-orange-300 hover:shadow-xl transition-all" data-testid={`category-preview-${idx}`}>
                    <div className="relative h-44 sm:h-56 overflow-hidden">
                      {previewImg ? (
                        <img src={previewImg} alt={category.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-orange-50 to-purple-50 flex items-center justify-center">
                          <Layers className="w-14 h-14 text-orange-300" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="font-bold text-white text-lg sm:text-xl mb-3">{category.name}</h3>
                      <Link to={`/products?category=${category.id}`}>
                        <Button className="bg-white/90 hover:bg-white text-gray-900 rounded-full px-5 py-2 text-sm font-semibold shadow-lg" data-testid={`view-all-products-${idx}`}>
                          View Products <ChevronRight className="ml-1.5 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12"><Grid3X3 className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-400">Loading categories...</p></div>
          )}
          
          <div className="text-center mt-10">
            <Link to="/products">
              <Button variant="outline" className="border-2 border-orange-300 text-orange-600 hover:bg-orange-50 rounded-full px-8 py-4 font-semibold">
                Sabhi Products Dekho <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section id="plans" className="py-16 sm:py-24 px-4 bg-white" data-testid="plans-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 border border-amber-200 text-amber-700 rounded-full text-sm font-medium mb-4">
              <Zap className="w-4 h-4" /> AI Subscription Plans
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              Apna <span className="text-orange-500">AI Plan</span> Choose Karo
            </h2>
            <p className="text-gray-500 text-lg">Higher plans = More AI levels = Maximum income</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {PLANS.map((plan, idx) => (
              <div key={idx} className={`relative group ${plan.popular ? 'lg:-translate-y-4' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-orange-500 text-white text-xs font-bold rounded-full shadow-lg z-10">MOST POPULAR</div>
                )}
                <Card className={`bg-white border-2 ${plan.popular ? 'border-orange-400 shadow-xl shadow-orange-100' : 'border-gray-200 hover:border-orange-200'} rounded-2xl overflow-hidden transition-all hover:shadow-lg h-full`}>
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${plan.color} flex items-center justify-center shadow-lg`}>
                      <span className="text-2xl font-bold text-white">{plan.type}</span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-1">{plan.price}</h3>
                    <p className="text-gray-400 text-sm mb-4">per month</p>
                    
                    <div className="bg-gray-50 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-500 text-sm">AI Levels</span>
                        <span className="text-orange-600 font-bold">{plan.levels}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-sm">Max Income</span>
                        <span className="text-purple-600 font-bold">{plan.income}</span>
                      </div>
                    </div>
                    
                    <Link to="/register">
                      <Button className={`w-full rounded-xl bg-gradient-to-r ${plan.color} hover:opacity-90 text-white py-5`}>Select Plan {plan.type}</Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-24 px-4 bg-gray-50" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 border border-orange-200 text-orange-700 rounded-full text-sm font-medium mb-6">
                <Building2 className="w-4 h-4" /> About Achraj Services
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                India Ka <span className="text-orange-500">Sabse Trusted</span> AI Network Platform
              </h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                <strong className="text-gray-900">Achraj Services Private Limited</strong> ne AI aur network marketing ko combine karke ek revolutionary income system banaya hai jo har Indian middle class family ke liye hai.
              </p>
              <p className="text-gray-500 mb-6 leading-relaxed">
                "Mehnat ek baar karo, kamaai baar baar pao." Hamara AI aapke liye 24/7 kaam karta hai — aap sote raho, network badhta rahe.
              </p>
              
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <Shield className="w-5 h-5 text-green-500" /><span className="text-sm text-gray-700">MCA Registered</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <Brain className="w-5 h-5 text-orange-500" /><span className="text-sm text-gray-700">AI Powered</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <Users className="w-5 h-5 text-purple-500" /><span className="text-sm text-gray-700">10000+ Members</span>
                </div>
              </div>
            </div>
            
            {/* Stats Panel */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-gray-900 font-bold">Platform Statistics</h3>
                  <p className="text-sm text-gray-500">Real-time AI performance</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {[
                  { label: "Total Members", value: "10,000+", color: "text-orange-600" },
                  { label: "AI Levels Active", value: "20", color: "text-purple-600" },
                  { label: "Max Earning Potential", value: "₹4.19 Cr", color: "text-amber-600" },
                  { label: "Network Uptime", value: "99.9%", color: "text-green-600" },
                ].map((stat, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <span className="text-gray-500">{stat.label}</span>
                    <span className={`font-bold ${stat.color}`}>{stat.value}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Award className="w-4 h-4 text-amber-500" />
                  <span>CIN: U47912UP2026PTC244373</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-r from-orange-500 to-purple-600 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-white">
            Taiyaar Ho? AI Activate Karo!
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            10000+ log already AI income se kama rahe hain. Aap kab shuru kar rahe ho?
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/register">
              <Button className="w-full sm:w-auto bg-white text-orange-600 hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl" data-testid="cta-join-btn">
                <Rocket className="mr-2 w-5 h-5" /> Abhi Join Karo - Free
              </Button>
            </Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <Mail className="w-5 h-5" /> {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="Achraj Logo" className="h-12 w-12 rounded-full object-cover border-2 border-orange-200" />
                <div><span className="font-bold text-gray-900">Achraj Services</span><p className="text-xs text-gray-500">Private Limited</p></div>
              </div>
              <p className="text-sm text-gray-500 mb-4">AI-powered network marketing platform — har Indian ke liye passive income ka rasta.</p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><Link to="/products" className="hover:text-orange-600 transition-colors">Products</Link></li>
                <li><a href="#plans" className="hover:text-orange-600 transition-colors">Plans</a></li>
                <li><a href="#about" className="hover:text-orange-600 transition-colors">About</a></li>
                <li><Link to="/shop" className="hover:text-orange-600 transition-colors">Shop</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">AI Features</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li>20-Level Matrix</li>
                <li>Auto-Fill System</li>
                <li>24/7 Operation</li>
                <li>Smart Optimization</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-orange-500" /><a href="tel:+919956864243" className="hover:text-orange-600">+91 99568 64243</a></li>
                <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-orange-500" /><a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-orange-600">{CONTACT_EMAIL}</a></li>
                <li className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-green-500" /><a href="https://wa.me/919956864243" target="_blank" rel="noopener noreferrer" className="hover:text-green-600">WhatsApp Support</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-8">
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs text-gray-400 mb-4">
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-green-500" /><span>Achraj Services Private Limited</span></div>
              <div className="flex items-center gap-2"><Award className="w-4 h-4 text-purple-500" /><span className="font-mono">CIN: U47912UP2026PTC244373</span></div>
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><span>MCA Registered</span></div>
            </div>
            <p className="text-center text-xs text-gray-400">© 2026 Achraj Services Private Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/919956864243?text=Hi%20Achraj%2C%20mujhe%20jankari%20chahiye" target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg shadow-green-200 transition-all hover:scale-110" data-testid="whatsapp-float-btn">
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>
    </div>
  );
}
